import { Checkbox, FormControlLabel, FormLabel, Grid, Stack, Typography } from '@mui/material';
import CustomTimeRangePicker from 'common/components/CustomTimeRangePicker';
import useFlexBoxStyles from 'common/flexBox';
import { useIncludeDateValidation } from 'common/hooks/useIncludeDateValidation';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => {
    return {
        unavailableText: {
            paddingRight: '14%',
            height: theme.spacing(4.5),
        },
        paddingTop75: {
            paddingTop: theme.spacing(0.75),
        },
    };
});

const transformDates = (dates) => {
    return dates.map((dateRange) => ({
        startDate: dateRange.startTime,
        endDate: dateRange.endTime,
    }));
};

const WorkingHours = ({ fields, handleFieldChange }) => {
    const { t } = useTranslation();
    const { classes } = useStyles();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const { isValidHourRange, error, validationType, setError } = useIncludeDateValidation();

    const weeklySchedule = fields?.weeklySchedule;

    const handleEditDate = (newDate, idx, dayIndex) => {
        const dates = weeklySchedule[dayIndex] || [];
        const newRange = [
            ...dates.slice(0, idx),
            { startTime: newDate.start, endTime: newDate.end },
            ...dates.slice(idx + 1),
        ];
        saveRange(newRange, dayIndex);
    };

    const handleAddDate = (newDate, dayIndex) => {
        const dates = weeklySchedule[dayIndex] || [];
        const newRange = [...dates, { startTime: newDate.start, endTime: newDate.end }];
        saveRange(newRange, dayIndex);
    };

    const handleDeleteDate = (idx, dayIndex) => {
        deleteError(idx, dayIndex);
        const dates = weeklySchedule[dayIndex] || [];
        const newRange = [...dates.slice(0, idx), ...dates.slice(idx + 1)];
        saveRange(newRange, dayIndex);
    };

    const deleteError = (idx, dayIndex) => {
        if (error?.idx === idx && error?.dayIndex === dayIndex) {
            setError(null);
        }
    };

    const saveRange = (data, dayIndex) => {
        handleFieldChange({
            target: {
                name: 'weeklySchedule',
                value: { ...weeklySchedule, [dayIndex]: data },
            },
        });
    };

    const checkBoxChange = (e, dayIndex) => {
        handleFieldChange({
            target: {
                name: 'weeklySchedule',
                value: {
                    ...weeklySchedule,
                    [dayIndex]: e.target.checked ? [{ startTime: moment().startOf('day').format('HH:mm'), endTime: moment().endOf('day').format('HH:mm') }] : [],
                },
            },
        });
    };

    const handleChange = (date, idx, dayIndex, callback) => {
        const ranges = transformDates(weeklySchedule[dayIndex]);
        const isValid = isValidHourRange({ ranges, date, idx, dayIndex, type: validationType.UNSELECTED });
        if (isValid) {
            if (idx === -1) {
                handleAddDate(date, dayIndex);
                callback?.();
            } else {
                handleEditDate(date, idx, dayIndex);
            }
        }
    };

    return (
        <Stack direction="column" spacing={1}>
            <FormLabel>{t('calendar.setWeeklyHours').toUpperCase()}</FormLabel>

            <Stack direction="column" spacing={2}>
                {Object.keys(weeklySchedule).map((dayIndex) => (
                    <Grid container key={dayIndex}>
                        <Grid item xs={3} alignItems="center">
                            <FormControlLabel
                                className={classes.paddingTop75}
                                control={
                                    <Checkbox
                                        className={cx(flexBox.padding0, flexBox.margin01)}
                                        checked={Boolean(weeklySchedule[dayIndex].length)}
                                        onChange={(e) => checkBoxChange(e, dayIndex)}
                                        inputProps={{ 'aria-label': 'disabled checkbox' }}
                                    />
                                }
                                label={<Typography>{moment().day(dayIndex).format('dddd').toUpperCase()}</Typography>}
                            />
                        </Grid>
                        <Grid item xs={9}>
                            <>
                                {weeklySchedule[dayIndex].length ? (
                                    <Stack direction="column" spacing={1}>
                                        {weeklySchedule[dayIndex]?.map((hours, idx) => (
                                            <div key={idx}>
                                                {hours ? (
                                                    <CustomTimeRangePicker
                                                        date={{ start: hours.startTime, end: hours.endTime }}
                                                        onApply={(date) => handleChange(date, idx, dayIndex)}
                                                        onDelete={() => handleDeleteDate(idx, dayIndex)}
                                                        error={error?.idx === idx ? error?.msg : null}
                                                        disabled={
                                                            error
                                                                ? error?.idx !== idx || error.dayIndex !== dayIndex
                                                                : false
                                                        }
                                                        dates={transformDates(weeklySchedule[dayIndex])}
                                                    />
                                                ) : null}
                                            </div>
                                        ))}

                                        <Stack
                                            direction="column"
                                            style={
                                                weeklySchedule[dayIndex].length === 0 ? {} : { paddingRight: '36px' }
                                            }
                                        >
                                            <CustomTimeRangePicker
                                                onApply={(date) => handleChange(date, -1, dayIndex)}
                                                error={error?.idx === -1 ? error?.msg : null}
                                                disabled={
                                                    error ? error?.idx !== -1 || error.dayIndex !== dayIndex : false
                                                }
                                                dates={transformDates(weeklySchedule[dayIndex])}
                                            />
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Stack
                                        direction="column"
                                        spacing={1}
                                        justifyContent="center"
                                        alignItems="center"
                                        className={classes.unavailableText}
                                    >
                                        <Typography variant="subtitle2" fontWeight={400}>
                                            {t('common.term.unavailable')}
                                        </Typography>
                                    </Stack>
                                )}
                            </>
                        </Grid>
                    </Grid>
                ))}
            </Stack>
        </Stack>
    );
};

export default WorkingHours;
